import { PolfEventPokerStatusEnum } from '@polf/shared/codegen/types'
import { formatChips, formatChipsShort } from '@polf/shared/locale'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import type { PokerLevelFragment } from '../../../__generated__/hooks'
import { useWakeLock } from '../../../hooks/useWakeLock'

const formatMmSs = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const m = Math.floor(safe / 60)
  const s = safe % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const computeRemaining = ({
  startedAt,
  pausedAt,
  durationMinutes,
  now,
}: {
  startedAt: Date | null
  pausedAt: Date | null
  durationMinutes: number
  now: number
}): number => {
  if (!startedAt) return durationMinutes * 60
  const effectiveNow = pausedAt ? pausedAt.getTime() : now
  const elapsedSec = (effectiveNow - startedAt.getTime()) / 1000
  return Math.max(0, durationMinutes * 60 - elapsedSec)
}

export type MoveNotice = {
  fromTable: number
  fromSeat: number
  toTable: number
  toSeat: number
}

/**
 * Full-viewport clock — the "projector in your pocket" surface.
 *
 * Use case: player puts the phone face-up on the rail (typically in
 * landscape), eats, smokes, chats. Phone stays awake via the wake lock
 * held by `ActiveView`. The clock dominates so it's readable from across
 * the table even at oblique angles.
 *
 * Layout contract — height-budgeted so the footer NEVER lands below the
 * fold in landscape:
 *   • Wrapper: `h-dvh` so we anchor to the actual viewport, not the
 *     larger HTML root that creates the "below fold" bug.
 *   • Centre (clock + blinds) uses `flex-1 min-h-0` so it surrenders
 *     space to top bar + footer rather than pushing them off-screen.
 *   • Clock font: `clamp(72px, min(22vw, 30vh), 240px)` — the height
 *     limiter is the saver in landscape where 30vh wins over 22vw.
 *   • Move notice slots between top bar and centre when present, so
 *     "you're moving" is loud but doesn't push the clock off-screen.
 *
 * Portal to `document.body` to escape every stacking context and the
 * bottom-nav. Press X or Escape to collapse back to the in-page card.
 * Body scroll is locked while open so a stray swipe doesn't drag the
 * underlying content visible behind the overlay.
 */
const ClockFullscreen = ({
  open,
  level,
  nextLevel,
  inBreak,
  startedAt,
  pausedAt,
  levelIndex,
  status,
  playersLeft,
  playersTotal,
  averageStack,
  myBB,
  avgBB,
  myPosition,
  moveNotice,
  onDismissMove,
  onClose,
}: {
  open: boolean
  level: PokerLevelFragment
  nextLevel: PokerLevelFragment | null
  inBreak: boolean
  startedAt: Date | null
  pausedAt: Date | null
  levelIndex: number
  status: PolfEventPokerStatusEnum
  playersLeft: number | null | undefined
  playersTotal: number | null | undefined
  averageStack: number | null | undefined
  myBB: number | null
  avgBB: number | null
  myPosition: number | null
  moveNotice: MoveNotice | null
  /** Clears the table-move notice. Wired to the same setState that the
   *  in-page TableMoveBanner uses, so dismissing in either surface
   *  hides the notice everywhere. */
  onDismissMove: () => void
  onClose: () => void
}) => {
  // Hold a screen wake lock while the fullscreen clock is open. ActiveView
  // already holds one, but the fullscreen clock is the "phone face-up on the
  // rail" surface — most used during pauses/breaks — so it owns its own lock to
  // stay awake regardless of which view opened it. Redundant locks are safe
  // (the screen stays awake while any sentinel is held).
  useWakeLock(open)

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!open) return undefined
    const id = window.setInterval(() => setNow(Date.now()), 500)
    return () => window.clearInterval(id)
  }, [open])

  // Body-scroll lock + Escape-to-close while open. Effect bails if `open`
  // is false so the listener doesn't leak across mounts.
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  if (!open) return null

  const activeDuration = inBreak
    ? level.breakAfterMinutes ?? 0
    : level.durationMinutes
  const remaining = computeRemaining({
    startedAt,
    pausedAt,
    durationMinutes: activeDuration,
    now,
  })

  const isPaused = status === PolfEventPokerStatusEnum.Paused
  const lowTime = remaining <= 60 && !isPaused && !inBreak
  const urgent = remaining <= 10 && !isPaused && !inBreak
  let clockColor = 'text-paper'
  if (lowTime) clockColor = urgent ? 'text-flag' : 'text-warn'

  let stateLabel: string | null = null
  if (isPaused) stateLabel = 'PAUSE'
  else if (inBreak) stateLabel = 'PAUSE — NIVÅ-PAUSE'

  const totalField = playersTotal != null ? playersTotal : playersLeft ?? null

  return createPortal(
    <div
      // Top-most overlay. z-[90] safely clears the bottom-nav (z-50) and
      // the stack-edit sheet (z-[60]). `h-dvh` resolves to the dynamic
      // viewport height so iOS Safari's collapsing toolbar can't push the
      // footer below the visible area.
      className="fixed inset-0 z-[90] text-paper flex flex-col h-dvh"
      style={{
        background: 'linear-gradient(180deg, #0e2418 0%, #050d09 100%)',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Pokerklokke i fullskjerm"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 90% 0%, rgba(230, 65, 24, 0.18), transparent 55%)',
        }}
      />

      {/* Top bar — Nivå (left) · position pill (middle) · close (right).
          flex-shrink-0 so the row's height stays predictable. */}
      <div className="relative flex items-center justify-between gap-3 px-5 pt-3 pb-1 shrink-0">
        <div className="min-w-0">
          <div className="text-[10px] up text-green-100/70 leading-none">
            Nivå
          </div>
          <div className="font-display font-bold text-lg mt-0.5 leading-none">
            #{levelIndex + 1}
          </div>
        </div>
        {myPosition != null ? (
          <div className="text-center">
            <div className="text-[10px] up text-green-100/70 leading-none">
              Din plass
            </div>
            <div className="font-display font-bold text-lg mt-0.5 leading-none num">
              {myPosition}
              {totalField != null ? (
                <span className="text-green-100/55"> / {totalField}</span>
              ) : null}
            </div>
          </div>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          aria-label="Lukk fullskjerm"
          className="text-paper/80 active:opacity-60 px-3 py-2 rounded-full bg-paper/10 backdrop-blur-sm shrink-0"
        >
          <span aria-hidden className="text-xl leading-none">
            ×
          </span>
        </button>
      </div>

      {/* Move notice — gold strip between top bar and clock when the
          floor balanced tables and the player's seat changed. Compact so
          it doesn't squeeze the clock below readable size in landscape. */}
      {moveNotice ? (
        <div className="relative mx-3 mt-2 rounded-md bg-gold text-ink-900 px-3 py-2 flex items-center gap-2 text-sm font-semibold shrink-0">
          <span aria-hidden className="text-base">
            🪑
          </span>
          <span className="flex-1 min-w-0 num">
            Flyttes til Bord{' '}
            <span className="font-mono num font-bold">
              {moveNotice.toTable}
            </span>{' '}
            · Sete{' '}
            <span className="font-mono num font-bold">{moveNotice.toSeat}</span>{' '}
            <span className="text-ink-700 font-normal">
              (fra {moveNotice.fromTable}/{moveNotice.fromSeat})
            </span>
          </span>
          <button
            type="button"
            onClick={onDismissMove}
            aria-label="Lukk varsel om bordbytte"
            className="text-ink-700 active:opacity-70 shrink-0 -mr-1 p-1"
          >
            <span aria-hidden className="text-lg leading-none">
              ×
            </span>
          </button>
        </div>
      ) : null}

      {/* Centre — clock + blinds + my-stack subline. flex-1 + min-h-0 so
          it absorbs leftover height; never expands beyond what's
          available (footer is always reachable). */}
      <div className="relative flex-1 min-h-0 flex flex-col items-center justify-center px-5 gap-3">
        {stateLabel ? (
          <div className="font-display font-bold text-warn text-xl tracking-wider">
            {stateLabel}
          </div>
        ) : null}
        <div
          className={`font-display font-extrabold leading-none num ${clockColor}`}
          style={{
            // Height-aware clamp — the `min(22vw, 30vh)` ensures the
            // clock shrinks in landscape (where vh is the limiter)
            // before it shoves the footer off the visible area.
            fontSize: 'clamp(72px, min(22vw, 30vh), 240px)',
            letterSpacing: '-0.05em',
            animation: urgent
              ? 'polf-pulse-red 0.9s ease-in-out infinite'
              : undefined,
          }}
        >
          {formatMmSs(remaining)}
        </div>
        {!inBreak ? (
          <div className="font-display font-bold num text-green-100/95 text-2xl tracking-tight text-center">
            {formatChips(level.smallBlind)} / {formatChips(level.bigBlind)}
            {level.ante > 0 ? (
              <span className="ml-2 text-base text-green-100/60">
                BB ante {formatChips(level.ante)}
              </span>
            ) : null}
          </div>
        ) : null}
        {/* My stack vs field — the comparison that drives most poker
            decisions. Sits directly under the blinds so the player can
            think "blinds 25/50, I have 14 BB, avg 22" in one glance. */}
        {myBB != null || avgBB != null ? (
          <div className="text-[13px] text-green-100/85 flex items-center justify-center gap-3 num">
            {myBB != null ? (
              <span>
                Du{' '}
                <span className="font-mono num font-bold text-paper">
                  {myBB} BB
                </span>
              </span>
            ) : null}
            {myBB != null && avgBB != null && avgBB > 0 ? (
              <span className="text-green-100/40">·</span>
            ) : null}
            {avgBB != null && avgBB > 0 ? (
              <span className="text-green-100/70">
                snitt{' '}
                <span className="font-mono num font-bold text-paper">
                  {avgBB} BB
                </span>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Footer — field stats + next level peek. Always visible: shrink-0
          + parent's h-dvh enforces it never gets clipped. */}
      <div className="relative px-5 pb-3 pt-1 flex flex-col gap-1 text-center shrink-0">
        {playersLeft != null || (averageStack != null && averageStack > 0) ? (
          <div className="text-xs text-green-100/80 flex items-center justify-center gap-3">
            {playersLeft != null ? (
              <span>
                <span className="font-mono num font-bold text-paper">
                  {playersLeft}
                  {playersTotal != null ? `/${playersTotal}` : ''}
                </span>{' '}
                igjen
              </span>
            ) : null}
            {playersLeft != null && averageStack != null && averageStack > 0 ? (
              <span className="text-green-100/40">·</span>
            ) : null}
            {averageStack != null && averageStack > 0 ? (
              <span>
                snitt{' '}
                <span className="font-mono num font-bold text-paper">
                  {formatChipsShort(averageStack)}
                </span>
              </span>
            ) : null}
          </div>
        ) : null}
        {/* Next-up peek: from a game level with a break attached →
            "Pause"; else → next level's blinds. From a break → next
            level's blinds (the level after the break). */}
        {(() => {
          if (inBreak) {
            return nextLevel ? (
              <div className="text-[11px] text-green-100/60">
                Neste:{' '}
                <span className="font-semibold text-paper">
                  {formatChips(nextLevel.smallBlind)}/
                  {formatChips(nextLevel.bigBlind)}
                </span>{' '}
                · om {Math.ceil(remaining / 60)} min
              </div>
            ) : null
          }
          if ((level.breakAfterMinutes ?? 0) > 0) {
            return (
              <div className="text-[11px] text-green-100/60">
                Neste: <span className="font-semibold text-paper">Pause</span> ·
                om {Math.ceil(remaining / 60)} min
              </div>
            )
          }
          return nextLevel ? (
            <div className="text-[11px] text-green-100/60">
              Neste:{' '}
              <span className="font-semibold text-paper">
                {formatChips(nextLevel.smallBlind)}/
                {formatChips(nextLevel.bigBlind)}
              </span>{' '}
              · om {Math.ceil(remaining / 60)} min
            </div>
          ) : null
        })()}
      </div>
    </div>,
    document.body
  )
}

export default ClockFullscreen
